/**
 * 틱톡 광고 성과 연동 설정 — 광고주 인가(OAuth) 와 몰에 붙일 광고 계정 선택.
 *
 * 흐름은 구글 연동 화면(`setup/google.tsx`)과 같다: 버튼 → 팝업 동의 → 팝업 닫힘 감시 → 상태 재조회.
 * 다른 점은 **계정 선택이 우리 쪽에 있다**는 것이다 — 틱톡 동의 화면에는 계정 선택이 없고(실측),
 * 인가된 광고 계정이 여럿이면 콜백이 아무것도 고르지 않은 채 `pending_selection` 으로 남긴다.
 * 그 상태에서는 수집이 돌지 않으므로 **선택 대기를 화면이 분명히 말해야** 한다.
 *
 * ⚠️ 스코프는 읽기 전용 3개(리포팅)뿐이다 — 이 화면에서 틱톡으로 무언가를 보내는 기능은 없다.
 * 연동 해제는 틱톡 쪽 토큰 회수를 **시도**하고 우리 저장분을 지운다. 회수가 확인되지 않으면 틱톡 쪽
 * (Settings > Authorizations > Remove)에서 직접 지우라고 안내한다(구글 해제와 같은 모양).
 */
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { AlertTriangle, ExternalLink, Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'

import { getGetShopQueryKey } from '@/api/generated/v2-shop/v2-shop'
import {
  disconnectTikTok,
  getGetTikTokStatusQueryKey,
  getTikTokStatus,
  getListTikTokAdvertisersQueryKey,
  tiktokOAuthAuthorize,
  useGetTikTokStatus,
  useListTikTokAdvertisers,
  useSelectTikTokAdvertiser,
} from '@/api/generated/v2-tiktok/v2-tiktok'
import { HiddenFeatureNotFound } from '@/components/HiddenFeatureNotFound'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Select, SelectOption } from '@/components/ui/Select'
import { SetupPage } from '@/features/setup/SetupPage'
import { useConfirm } from '@/hooks/useConfirm'
import { apiMessage } from '@/lib/apiError'
import { auth } from '@/lib/auth'
import { useShop } from '@/lib/shopContext'

export const Route = createFileRoute('/_dashboard/setup/tiktok')({
  component: TikTokSetupPage,
})

/**
 * ⭐ 히든 게이트 — 실험실 플래그(`shops.lab_features_enabled`, 어드민 토글)가 꺼진 샵이면 **첫 줄에서** 잘라낸다.
 *    본문 컴포넌트를 분리해 둬서 허용 밖이면 쿼리도 나가지 않는다(구글 화면과 같은 구조).
 * ⚠️ 이 플래그는 노출용이다 — 실제 차단은 `/v2/tiktok/*` 404(`v2/tiktok/gate.py`)가 한다.
 */
function TikTokSetupPage() {
  const { shop } = useShop()
  if (!shop?.tiktok_enabled) return <HiddenFeatureNotFound />
  return <TikTokSetupContent />
}

/** react-query 에러에서 HTTP 상태를 꺼낸다 — custom-instance 는 두 모양 중 하나로 던진다. */
function errorStatus(err: unknown): number | undefined {
  const e = err as { status?: number; response?: { status?: number } } | null
  return e?.status ?? e?.response?.status
}

function TikTokSetupContent() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { confirm, ConfirmDialog } = useConfirm()

  useEffect(() => {
    if (!auth.isAuthenticated()) navigate({ to: '/' })
  }, [navigate])

  const {
    data: status,
    isLoading,
    error: statusError,
    refetch: refetchStatus,
    isFetching: statusFetching,
  } = useGetTikTokStatus({
    query: {
      enabled: auth.isAuthenticated(),
      // 404(허용 목록 밖)는 재시도하지 않는다 — 일시적 5xx·네트워크 오류는 그대로 재시도한다
      retry: (count, err) => errorStatus(err) !== 404 && count < 3,
    },
  })
  // 플래그가 늦게 반영된 경우(샵 응답 캐시 등)에도 API 가 404 면 같은 화면으로 떨어뜨린다
  const statusNotFound = errorStatus(statusError) === 404
  // ⚠️ 상태를 **모르는** 것과 미연동은 다르다. 조회가 실패해 데이터가 없으면 `connected` 가 false 로
  //    계산되는데, 그대로 그리면 연동돼 수집 중인 샵이 "미연동" 을 보고 재연동하러 간다(재연동은
  //    후보가 여럿이면 선택 대기로 떨어질 수 있어 무해하지 않다). 그때는 에러와 재시도만 그린다.
  const statusUnknown = !!statusError && !status && !statusNotFound

  // 상태와 후보는 **함께** 낡는다 — 재연동하면 토큰이 바뀌어 후보도 달라진다.
  // 샵 응답도 함께 — 사이드바 완료·경고 표시가 `shop.tiktok` 요약을 읽는다.
  const refreshAll = () => {
    queryClient.invalidateQueries({ queryKey: getGetTikTokStatusQueryKey() })
    queryClient.invalidateQueries({ queryKey: getListTikTokAdvertisersQueryKey() })
    queryClient.invalidateQueries({ queryKey: getGetShopQueryKey() })
  }

  // 팝업이 아니라 같은 탭으로 돌아온 경우의 복귀 신호 처리
  // (콜백 `_close_popup_response` 가 `/setup/tiktok?tiktok_return=1…` 로 보낸다).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('tiktok_return') !== '1') return
    if (params.get('tiktok_ok') !== '1') {
      toast.error(callbackErrorMessage(params.get('tiktok_err')))
    } else if (params.get('tiktok_pending') === '1') {
      // 연동은 됐지만 수집은 아직 안 돈다 — "완료" 로 말하지 않는다
      toast.success('틱톡 계정이 연동되었어요. 광고 계정을 선택해 주세요')
    } else {
      toast.success('틱톡 계정이 연동되었어요')
    }
    queryClient.invalidateQueries({ queryKey: getGetTikTokStatusQueryKey() })
    queryClient.invalidateQueries({ queryKey: getListTikTokAdvertisersQueryKey() })
    queryClient.invalidateQueries({ queryKey: getGetShopQueryKey() })
    // 새로고침해도 토스트가 반복되지 않게 쿼리스트링을 지운다
    window.history.replaceState({}, '', window.location.pathname)
  }, [queryClient])

  // ⚠️ 서버에선 GET 이지만 **버튼을 눌렀을 때만** 부른다 — state 에 TTL(30분)이 있어 마운트 때
  //    만들어 두면 늦게 누른 사용자가 `invalid_state` 로 떨어진다(구글 화면과 같은 이유).
  const connectMutation = useMutation({
    mutationFn: () => tiktokOAuthAuthorize(),
    onSuccess: ({ authorize_url }) => {
      // 팝업이 막히면 같은 탭으로 진행한다 — 콜백이 두 경우 모두를 처리한다.
      const popup = window.open(authorize_url, 'tiktok-oauth', 'width=520,height=720')
      if (!popup) {
        window.location.href = authorize_url
        return
      }
      // ⚠️ 팝업이 정상적으로 닫히면 부모는 아무 신호를 못 받는다. `postMessage` 는 COOP 로
      //    `window.opener` 가 끊길 수 있어 쓰지 않고, **우리가 연 창의 `closed`** 를 감시한다.
      const timer = window.setInterval(() => {
        if (!popup.closed) return
        window.clearInterval(timer)
        refreshAll()
        // ⚠️ 팝업 경로의 실패는 **사유가 부모에 오지 않는다** — 콜백 HTML 이 먼저 `window.close()` 를
        //    하므로 같은 탭 복귀(`tiktok_err`)가 일어나지 않는다. 틱톡은 `no_advertiser`(권한 있는
        //    광고 계정이 없는 아이디)가 흔한 실패라, 닫힌 뒤에도 미연동이면 최소한의 안내를 띄운다.
        //    (재연동 실패는 이전 연동이 남아 있어 여기서 구분할 수 없다 — 화면이 그대로인 것이 맞다.)
        queryClient
          .fetchQuery({ queryKey: getGetTikTokStatusQueryKey(), queryFn: () => getTikTokStatus() })
          .then((next) => {
            if (!next.connected) {
              toast.error(
                '연동이 완료되지 않았어요. 광고 계정 권한이 있는 틱톡 아이디로 동의했는지 확인해 주세요',
              )
            }
          })
          .catch(() => {})
      }, 500)
      // 팝업을 열어둔 채 잊어버려도 타이머가 남지 않게 state TTL(30분)과 맞춘다.
      window.setTimeout(() => window.clearInterval(timer), 30 * 60 * 1000)
    },
    onError: (e: unknown) => toast.error(apiMessage(e, '틱톡 연동을 시작하지 못했어요')),
  })

  const disconnectMutation = useMutation({
    mutationFn: () => disconnectTikTok(),
    onSuccess: (result) => {
      refreshAll()
      // ⚠️ 회수가 확인되지 않았으면 그렇게 말한다 — "해제됐어요" 만 보이면 고객사 틱톡 Authorizations 에
      //    우리 앱이 남아 있는 걸 모른다.
      if (result.revoked_at_tiktok) {
        toast.success('틱톡 연동이 해제되었어요')
      } else {
        toast.success(
          '연동을 해제했어요. 틱톡 쪽 권한 회수는 확인되지 않았으니 틱톡 광고 관리자 Settings > Authorizations 에서 지워 주세요',
          { duration: 8000 },
        )
      }
    },
    // 거절 사유("방금 바뀌었어요"·"이미 해제된")는 화면 상태가 낡았다는 뜻이다 — 다시 읽는다
    onError: (e: unknown) => {
      toast.error(apiMessage(e, '연동을 해제하지 못했어요'))
      refreshAll()
    },
  })

  const connected = !!status?.connected
  // ⚠️ 연동됨과 자격증명 유효성은 다른 축이다. `invalid` 와 `pending_selection` 은 **동시에 참**일 수
  //    있고, 그때는 invalid 를 먼저 본다 — 무효 토큰으로는 후보 조회도 실패하므로 재연동이 먼저다.
  const credentialInvalid = connected && !!status?.invalid
  const pendingSelection = connected && !credentialInvalid && !!status?.pending_selection

  const guide = (
    <div className="space-y-4 text-sm text-on-surface-variant">
      <p>
        틱톡 광고 계정을 연동하면 캠페인·광고별 성과(노출·클릭·비용)를 매일 자동으로
        불러와요. 조회 권한만 요청하며, 광고를 수정하거나 만들지 않아요.
      </p>
      <ol className="list-decimal pl-5 space-y-2">
        <li>&lsquo;틱톡 계정 연동&rsquo; 버튼을 눌러 권한을 허용해 주세요.</li>
        <li>
          광고 계정이 여러 개면 이 쇼핑몰에 연동할 계정을 하나 골라 저장해 주세요.
          <span className="block text-xs mt-1">
            목록에 원하는 계정이 없으면 그 계정 권한이 있는 틱톡 아이디로 다시 연동해 주세요.
          </span>
        </li>
      </ol>
      <p className="text-xs">
        연동을 해제하면 틱톡 쪽 권한도 함께 회수해요. 회수가 확인되지 않으면 틱톡 광고 관리자의
        Settings &gt; Authorizations 에서 직접 지울 수 있어요.
      </p>
    </div>
  )

  if (statusNotFound) return <HiddenFeatureNotFound />

  if (statusUnknown) {
    return (
      <div className="max-w-xl mx-auto py-16 px-4">
        <div className="flex gap-2 p-4 rounded-lg bg-red-50 text-red-900 text-sm">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            <p>
              {apiMessage(statusError, '틱톡 연동 상태를 불러오지 못했어요. 잠시 후 다시 시도해 주세요')}
            </p>
            <button
              type="button"
              onClick={() => refetchStatus()}
              disabled={statusFetching}
              className="mt-2 min-h-[44px] sm:min-h-0 text-sm font-medium underline"
            >
              다시 시도
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-on-surface-variant" />
      </div>
    )
  }

  return (
    <SetupPage
      title="틱톡 광고 연동"
      connected={connected}
      warningBadge={
        credentialInvalid ? '재연동 필요' : pendingSelection ? '광고 계정 선택 필요' : undefined
      }
      externalLink="https://ads.tiktok.com/"
      externalLinkLabel="틱톡 광고 관리자 열기"
      guide={guide}
      // 연동 해제 — 공통 틀 ⑤(맨 아래 회색 텍스트). 규칙은 agent_docs/connect_ux.md §2
      disconnect={
        connected
          ? {
              description: '연동을 해제하면 광고 성과 수집이 멈추고, 틱톡 계정에서도 권한을 회수해요.',
              loading: disconnectMutation.isPending,
              onClick: async () => {
                const ok = await confirm(
                  '광고 성과 수집이 중단되고, 틱톡 계정에서도 이 앱의 권한을 회수해요. 다시 쓰려면 연동과 광고 계정 선택을 새로 해야 해요.',
                  { title: '틱톡 연동을 해제할까요?', confirmText: '연동 해제', variant: 'danger' },
                )
                if (ok) disconnectMutation.mutate()
              },
            }
          : undefined
      }
    >
      <div className="space-y-6">
        {/* 재조회가 실패했지만 직전 상태는 있는 경우 — 보이는 상태가 낡았을 수 있음을 알린다
            (데이터가 아예 없는 실패는 위에서 따로 그린다) */}
        {statusError && !statusNotFound && (
          <div className="flex gap-2 p-4 rounded-lg bg-red-50 text-red-900 text-sm">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <p>{apiMessage(statusError, '연동 상태를 불러오지 못했어요. 잠시 후 다시 시도해 주세요')}</p>
          </div>
        )}

        {credentialInvalid && (
          <div className="flex gap-2 p-4 rounded-lg bg-red-50 text-red-900 text-sm">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              <p className="font-medium">틱톡 연동이 만료되었어요.</p>
              <p className="mt-1">
                광고 성과를 더 이상 불러오지 못해요. '틱톡 계정 다시 연동' 버튼으로 다시 연동해 주세요.
              </p>
            </div>
          </div>
        )}

        {pendingSelection && (
          <div className="flex gap-2 p-4 rounded-lg bg-yellow-50 text-yellow-900 text-sm">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <p>
              연동은 됐지만 광고 계정을 아직 고르지 않았어요. 계정을 골라야 성과 수집이
              시작돼요.
            </p>
          </div>
        )}

        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold text-on-surface">틱톡 계정</h2>
            {!connected ? (
              <Badge variant="secondary">미연동</Badge>
            ) : credentialInvalid ? (
              <Badge variant="warning">재연동 필요</Badge>
            ) : pendingSelection ? (
              <Badge variant="warning">광고 계정 선택 필요</Badge>
            ) : (
              <Badge variant="success">연동됨</Badge>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              // 정상 연동 상태의 '다시 연동' 은 주 행동이 아니다 — 만료 때만 강조한다
              variant={connected && !credentialInvalid ? 'outline' : 'primary'}
              onClick={() => connectMutation.mutate()}
              loading={connectMutation.isPending}
              className="min-h-[44px] sm:min-h-0"
            >
              <ExternalLink className="h-4 w-4 mr-1.5" />
              {connected ? '틱톡 계정 다시 연동' : '틱톡 계정 연동'}
            </Button>

          </div>
        </section>

        {/* 무효 토큰이면 후보 조회가 실패만 하므로 선택 섹션을 띄우지 않는다 */}
        {connected && !credentialInvalid && (
          <AdvertiserSection
            savedAdvertiserId={status?.advertiser_id ?? null}
            savedAdvertiserName={status?.advertiser_name ?? null}
            onReconnect={() => connectMutation.mutate()}
            reconnecting={connectMutation.isPending}
            onStale={refreshAll}
          />
        )}
      </div>
      <ConfirmDialog />
    </SetupPage>
  )
}

/**
 * 몰에 붙일 광고 계정을 **이 연동의 후보 목록에서** 고르게 한다.
 *
 * ⚠️ 후보는 저장된 값이 아니라 **매번 틱톡에 묻는다**(서버 `list_advertiser_candidates`).
 *    그래서 조회 실패가 흔한 모양이다 — **실패를 "계정 0개" 로 그리지 않는다.** 서버가 사유를
 *    고객 언어로 준다(서버 미설정 / 재연동 필요 / 잠시 후 재시도).
 */
function AdvertiserSection({
  savedAdvertiserId,
  savedAdvertiserName,
  onReconnect,
  reconnecting,
  onStale,
}: {
  savedAdvertiserId: string | null
  savedAdvertiserName: string | null
  onReconnect: () => void
  reconnecting: boolean
  // 저장이 거절되면 연동 자체가 바뀐 경우가 있다 — 상태와 후보를 함께 다시 읽는다
  onStale: () => void
}) {
  const queryClient = useQueryClient()
  const [advertiserId, setAdvertiserId] = useState(savedAdvertiserId ?? '')

  // 저장값이 바뀌면(재연동·다른 탭 선택) 드롭다운을 따라간다.
  // 매 렌더 동기화가 아니라 **저장값 변화에만** 반응하므로 고르는 중에 값이 되돌아가지 않는다.
  useEffect(() => {
    setAdvertiserId(savedAdvertiserId ?? '')
  }, [savedAdvertiserId])

  const {
    data: candidates,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useListTikTokAdvertisers({
    // 422 는 사유가 확정된 거절이라(설정·권한) 재시도해도 같다
    query: { retry: (count, err) => errorStatus(err) !== 422 && count < 2 },
  })
  const items = candidates?.items ?? []
  const selectedItem = items.find((a) => a.advertiser_id === advertiserId)

  const selectMutation = useSelectTikTokAdvertiser({
    mutation: {
      onSuccess: (next) => {
        queryClient.setQueryData(getGetTikTokStatusQueryKey(), next)
        queryClient.invalidateQueries({ queryKey: getListTikTokAdvertisersQueryKey() })
        // 사이드바 완료 표시(`shop.tiktok.advertiser_selected`)를 따라오게 한다
        queryClient.invalidateQueries({ queryKey: getGetShopQueryKey() })
        toast.success('광고 계정을 저장했어요')
      },
      // "연동이 방금 바뀌었어요" 같은 경합 거절도 여기로 온다 — 목록을 다시 읽게 한다.
      onError: (e: unknown) => {
        toast.error(apiMessage(e, '광고 계정을 저장하지 못했어요'))
        // ⚠️ 목록만이 아니라 **상태도** 다시 읽는다. 거절 사유 중 "연동이 방금 바뀌었어요" 와
        //    "먼저 연동해 주세요" 는 status 가 낡았다는 뜻인데, staleTime(5분) 동안 화면이 옛 상태로 남는다.
        onStale()
      },
    },
  })

  // 목록에서 확인된 계정만 저장한다 — 서버도 후보 밖 ID 를 거절하지만, 거절될 저장 버튼을
  // 누르게 두지 않는다. 이미 저장된 계정을 그대로 다시 저장할 이유도 없다.
  // ⚠️ 재조회 중(`isFetching`)에도 막는다 — 재연동 직후엔 **옛 토큰의 후보**가 화면에 남아 있어서
  //    그 목록으로 판정하면 곧 사라질 계정을 저장하게 된다(서버가 422 로 막지만 헛걸음이다).
  const canSave =
    !!selectedItem &&
    !isFetching &&
    advertiserId !== (savedAdvertiserId ?? '') &&
    !selectMutation.isPending

  // 저장된 계정이 지금 후보에 없다 — 틱톡 쪽에서 권한이 빠졌을 수 있다.
  // ⚠️ 조회가 실패했거나 아직 안 끝났으면(재조회 포함) 판단하지 않는다("묻지 못했다" ≠ "없다").
  //    재연동 직후 status 가 먼저 오면 새 저장값을 옛 후보와 비교해 경고가 깜빡인다.
  const savedMissing =
    !!savedAdvertiserId &&
    !isFetching &&
    !error &&
    !!candidates &&
    !items.some((a) => a.advertiser_id === savedAdvertiserId)

  return (
    <section className="space-y-4 pt-2 border-t border-outline-variant">
      <div>
        <h2 className="text-base font-semibold text-on-surface">광고 계정</h2>
        <p className="text-sm text-on-surface-variant mt-1">
          이 쇼핑몰의 성과로 불러올 틱톡 광고 계정을 하나 골라 주세요.
        </p>
        {savedAdvertiserId && (
          <p className="text-sm text-on-surface-variant mt-1">
            지금 연결된 계정:{' '}
            <span className="font-medium text-on-surface">
              {savedAdvertiserName
                ? `${savedAdvertiserName} (${savedAdvertiserId})`
                : savedAdvertiserId}
            </span>
          </p>
        )}
      </div>

      <Select
        label="틱톡 광고 계정"
        value={advertiserId}
        disabled={isFetching || !!error || items.length === 0}
        onChange={(e) => setAdvertiserId(e.target.value)}
        className="min-h-[44px] sm:min-h-0"
      >
        <SelectOption value="">
          {/* ⚠️ 네 상태를 구분한다 — 뭉치면 조회 실패가 "계정이 없어요" 로 둔갑한다 */}
          {isLoading
            ? '불러오는 중…'
            : error
              ? '광고 계정을 불러오지 못했어요'
              : items.length === 0
                ? '이 연동으로 볼 수 있는 광고 계정이 없어요'
                : '선택해 주세요'}
        </SelectOption>
        {items.map((a) => (
          <SelectOption key={a.advertiser_id} value={a.advertiser_id}>
            {a.advertiser_name
              ? `${a.advertiser_name} (${a.advertiser_id})`
              : a.advertiser_id}
          </SelectOption>
        ))}
      </Select>

      {error && (
        <div className="flex gap-2 p-4 rounded-lg bg-red-50 text-red-900 text-sm">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            <p>{apiMessage(error, '광고 계정 목록을 불러오지 못했어요')}</p>
            <div className="mt-2 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => refetch()}
                disabled={isFetching}
                className="min-h-[44px] sm:min-h-0 text-sm font-medium underline"
              >
                다시 시도
              </button>
              <button
                type="button"
                onClick={onReconnect}
                disabled={reconnecting}
                className="min-h-[44px] sm:min-h-0 text-sm font-medium underline"
              >
                다시 연동하기
              </button>
            </div>
          </div>
        </div>
      )}

      {!isLoading && !error && items.length === 0 && (
        <p className="text-sm text-on-surface-variant">
          광고 계정 권한이 있는 틱톡 아이디로 다시 연동해 주세요.
        </p>
      )}

      {savedMissing && (
        <p className="text-sm text-yellow-800">
          연결된 계정이 지금 목록에 없어요. 틱톡에서 권한이 바뀌었을 수 있어요 — 다른
          계정을 고르거나 다시 연동해 주세요.
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        <Button
          onClick={() => selectMutation.mutate({ data: { advertiser_id: advertiserId } })}
          loading={selectMutation.isPending}
          disabled={!canSave}
          className="min-h-[44px] sm:min-h-0"
        >
          저장
        </Button>
      </div>
    </section>
  )
}

/**
 * 콜백 실패 사유를 고객 언어로. 모르는 코드는 일반 문구로 흘린다.
 * ⚠️ 서버 `v2/tiktok/router.py::_SAFE_CALLBACK_ERRORS` 와 **1:1** 로 맞춘다 — 서버에 코드를 추가하면 여기도.
 */
function callbackErrorMessage(code: string | null): string {
  switch (code) {
    case 'invalid_state':
      return '연동 시간이 지났어요. 다시 시도해 주세요'
    case 'shop_not_found':
      return '쇼핑몰 정보를 찾지 못했어요. 새로고침 후 다시 시도해 주세요'
    case 'missing_parameters':
      return '틱톡 연동이 취소되었어요'
    case 'not_configured':
      return '틱톡 연동이 서버에 아직 설정되지 않았어요. 담당자에게 문의해 주세요'
    case 'token_exchange_failed':
      return '틱톡과 통신하지 못했어요. 잠시 후 다시 시도해 주세요'
    case 'no_advertiser':
      return '권한이 허용된 광고 계정이 없어요. 광고 계정 권한이 있는 아이디로 다시 연동해 주세요'
    case 'concurrent_callback':
      return '다른 창에서 연동이 동시에 진행됐어요. 새로고침 후 상태를 확인해 주세요'
    default:
      return '틱톡 연동에 실패했어요'
  }
}
